import compact from 'lodash/compact';
import keyBy from 'lodash/keyBy';
import { action, makeObservable, observable } from 'mobx';

import { postRequest } from '../../services/api';
import type { ApiResponse } from '../../services/types';
import { getImageUrlWithBucketPrefix } from '../../utils/images';
import type { Game } from '../game/types';

export class RecentlyPlayedStore {
  constructor() {
    makeObservable(this);
    this.syncLocalGameHistoryData();
  }

  @observable public recentlyPlayedIds: Game['masterCasinoGameId'][] = [];
  @action
  private setRecentlyPlayedIds(value: Game['masterCasinoGameId'][]) {
    this.recentlyPlayedIds = value;
  }

  @observable public recentlyPlayedGames: Game[] = [];
  @action
  private setRecentlyPlayedGames(value: Game[]) {
    this.recentlyPlayedGames = value;
  }

  private async getGamesByIds(gameIds: Game['masterCasinoGameId'][]) {
    if (!gameIds?.length) {
      return [];
    }

    const response = await postRequest<ApiResponse<{ games: Game[] }>>('games', {
      gameIds,
    });

    if (response.data.data?.games) {
      const gamesMap = keyBy(response.data.data.games, 'masterCasinoGameId');
      const rows = gameIds
        .filter((id) => !!gamesMap[id])
        .map((id) => {
          const game = gamesMap[id];

          return {
            ...game,
            thumbnailHrUrl: getImageUrlWithBucketPrefix(game.thumbnailHrUrl || ''),
            thumbnailUrl: getImageUrlWithBucketPrefix(game.thumbnailUrl || ''),
          };
        });

      return rows;
    }
    return [];
  }

  public getRecentlyPlayedGames = async () => {
    const games = await this.getGamesByIds(this.recentlyPlayedIds);
    this.setRecentlyPlayedGames(games);
    return games;
  };

  public localSavePlayedGameId(gameId: Game['masterCasinoGameId']) {
    try {
      const prevPlayedGamesIds = localStorage.getItem('game_played_ids');
      const parsedPrevPlayedGamesIds = prevPlayedGamesIds
        ? (JSON.parse(prevPlayedGamesIds) as Game['masterCasinoGameId'][])
        : [];
      const newPlayedGamesIdsSet = new Set([gameId, ...parsedPrevPlayedGamesIds]);
      const newPlayedGamesIdsArray = compact(Array.from(newPlayedGamesIdsSet).slice(0, 9));

      this.setRecentlyPlayedIds(newPlayedGamesIdsArray);
      localStorage.setItem('game_played_ids', JSON.stringify(newPlayedGamesIdsArray));
    } catch {}
  }

  private syncLocalGameHistoryData() {
    try {
      const gamePlayedIdsJSON = localStorage.getItem('game_played_ids');
      const gamePlayedIds = JSON.parse(gamePlayedIdsJSON || '[]') as Game['masterCasinoGameId'][];
      this.setRecentlyPlayedIds(compact(gamePlayedIds));
    } catch {
      this.setRecentlyPlayedIds([]);
    }
  }
}
